import { useState, useRef, Fragment, useEffect } from "react";
import { toast } from "react-hot-toast";
import { IoMdClose } from "react-icons/io";
import { ClipLoader } from "react-spinners";
import MediaPopup from "./modal/MediaPopup";
import DeleteModal from "./modal/DeleteModal";
import { GoHeart, GoHeartFill } from "react-icons/go";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import SkeletonTwo from "../../components/skeleton/SkeletonTwo";
import { IoSend, IoImages, IoChevronBackOutline } from "react-icons/io5";
import {
    getAllGroupMessages, sendGroupMessage, deleteGroupChat,
    leaveGroupChat, groupMessageReaction, markAllMessagesAsRead, muteUnmuteGroupChat
} from "../../redux/ApiSlice/MessageGroupSlice";

const DEFAULT_GROUP_IMAGE = "https://ui-avatars.com/api/?name=Group&background=random";

const MessageGroupChatRoom = () => {
    const { id } = useParams();
    const groupId = id;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const menuRef = useRef(null);
    const fileInputRef = useRef();
    const messagesEndRef = useRef(null);
    const [showMenu, setShowMenu] = useState(false);
    const [mute, setMute] = useState(false);
    const [showDeleteChat, setShowDeleteChat] = useState(false);
    const [message, setMessage] = useState("");
    const [image, setImage] = useState(null);
    const [video, setVideo] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [videoPreview, setVideoPreview] = useState(null);
    const [sending, setSending] = useState(false);
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupType, setPopupType] = useState(null);
    const [popupSrc, setPopupSrc] = useState("");
    const [hoveredVideo, setHoveredVideo] = useState(null);
    const [now, setNow] = useState(new Date());

    const { groupMessages, loading, sendGroupMessageLoading } = useSelector((state) => state.messageGroup);

    useEffect(() => {
        dispatch(getAllGroupMessages(groupId));
        setTimeout(() => {
            dispatch(markAllMessagesAsRead(groupId));
        }, 1000);
    }, [dispatch, groupId]);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "auto" });
        }
        // Update 'now' whenever messages change to ensure relative time is correct
        setNow(new Date());
    }, [groupMessages]);

    // Sync mute state with group's is_muted property
    useEffect(() => {
        if (groupMessages?.group?.is_muted !== undefined) {
            setMute(groupMessages.group.is_muted);
        }
    }, [groupMessages?.group?.is_muted]);

    useOutsideClick(menuRef, () => setShowMenu(false));

    const handleMuteGroup = (groupId) => {
        dispatch(muteUnmuteGroupChat(groupId));
        setTimeout(() => {
            dispatch(getAllGroupMessages(groupId));
        }, 1000);
        setShowMenu(false);
    };

    const menuItems = [
        {
            label: mute ? "Unmute" : "Mute",
            action: () => handleMuteGroup(groupId)
        },
        {
            label: "Leave",
            action: () => {
                dispatch(leaveGroupChat(groupId)).then(() => {
                    toast.success("Left group successfully");
                    navigate("/message", { state: { activeTab: "Groups" } });
                });
                setShowMenu(false);
            }
        },
        {
            label: "Delete",
            action: () => {
                setShowDeleteChat(true);
                setShowMenu(false);
            }
        },
    ];

    const handleFileClick = () => fileInputRef.current.click();

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (file.type.startsWith("image/")) {
                setImage(file);
                setImagePreview(URL.createObjectURL(file));
                setVideo(null);
                setVideoPreview(null);
            } else if (file.type.startsWith("video/")) {
                setVideo(file);
                setVideoPreview(URL.createObjectURL(file));
                setImage(null);
                setImagePreview(null);
            } else {
                toast.error("Please select an image or video file.");
            }
        }
    };

    const handleRemoveImage = () => {
        setImage(null);
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = null;
    };

    const handleRemoveVideo = () => {
        setVideo(null);
        setVideoPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (sending) return;
        const hasMessage = message.trim() !== "";
        const hasImage = !!image;
        const hasVideo = !!video;
        if (!hasMessage && !hasImage && !hasVideo) {
            toast.error("Please enter a message, image, or video.");
            return;
        }
        setSending(true);
        try {
            const formData = new FormData();
            if (hasMessage) formData.append("message", message);
            if (hasImage) formData.append("image", image);
            if (hasVideo) formData.append("video", video);
            await dispatch(sendGroupMessage({ groupId, messageData: formData }));
            dispatch(getAllGroupMessages(groupId));
            setMessage("");
            setImage(null);
            setImagePreview(null);
            setVideo(null);
            setVideoPreview(null);
            if (fileInputRef.current) fileInputRef.current.value = null;
        } catch (err) {
            toast.error("Failed to send message");
        }
        setSending(false);
    };

    const handleDelete = () => {
        dispatch(deleteGroupChat(groupId)).then(() => {
            toast.success("Group deleted successfully");
            navigate("/message", { state: { activeTab: "Groups" } });
        });
        setShowDeleteChat(false);
    };

    // Reaction handler for group chat
    const handleReaction = (messageId, isSentByCurrentUser) => {
        if (!isSentByCurrentUser) {
            dispatch(groupMessageReaction(messageId)).then(() => {
                dispatch(getAllGroupMessages(groupId));
            });
        }
    };

    // Helper to format created_at as relative time
    function formatRelativeTime(dateString, nowArg = now) {
        const now = nowArg;
        const date = new Date(dateString);

        // Calendar day comparison
        const nowYear = now.getFullYear();
        const nowMonth = now.getMonth();
        const nowDate = now.getDate();
        const dateYear = date.getFullYear();
        const dateMonth = date.getMonth();
        const dateDate = date.getDate();

        // Format time as h:mm AM/PM (uppercase)
        let timeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        timeString = timeString.replace(/am|pm/i, match => match.toUpperCase());

        // Helper for month abbreviation in caps
        const monthAbbr = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
        const day = date.getDate().toString().padStart(2, '0');
        const year = date.getFullYear();
        const weekday = date.toLocaleString('en-US', { weekday: 'long' });

        // Today
        if (nowYear === dateYear && nowMonth === dateMonth && nowDate === dateDate) {
            return `Today ${timeString}`;
        }
        // Yesterday
        const yesterday = new Date(now);
        yesterday.setDate(nowDate - 1);
        if (
            dateYear === yesterday.getFullYear() &&
            dateMonth === yesterday.getMonth() &&
            dateDate === yesterday.getDate()
        ) {
            return `Yesterday ${timeString}`;
        }
        // Same week
        const diffMs = now - date;
        const diffDay = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        if (diffDay < 7) return `${weekday} ${timeString}`;
        if (nowYear === dateYear) return `${day} ${monthAbbr} AT ${timeString}`;
        return `${day} ${monthAbbr} ${year}`;
    }

    if (loading) return <SkeletonTwo />;

    return (
        <Fragment>
            <div className="h-full">
                <div className="sticky top-0 z-50 bg-gray-900">
                    <div className="grid grid-cols-12 items-center px-1 py-1 border-b border-gray-400 dark:border-gray-800">
                        <div className="col-span-1 flex items-center justify-start">
                            <IoChevronBackOutline
                                onClick={() => navigate("/message", { state: { activeTab: "Groups" } })}
                                className="text-[22px] text-white dark:text-white cursor-pointer"
                            />
                        </div>
                        <div className="col-span-10 px-2">
                            <div className="flex items-center justify-center gap-x-2">
                                <img
                                    alt={groupMessages?.group?.name || "Group"}
                                    src={groupMessages?.group?.image || DEFAULT_GROUP_IMAGE}
                                    onClick={() => groupMessages?.group?.id && navigate(`/message/group-info/${groupMessages.group.id}`)}
                                    className="h-9 w-9 rounded-full object-cover border border-gray-800 dark:border-gray-800 cursor-pointer"
                                />
                                <div className="flex flex-col">
                                    <p
                                        onClick={() => groupMessages?.group?.id && navigate(`/message/group-info/${groupMessages.group.id}`)}
                                        className="text-[12px] line-clamp-1 font-semibold text-white dark:text-white cursor-pointer"
                                    >
                                        {groupMessages?.group?.name || "Group Chat"}
                                    </p>
                                    <p
                                        onClick={() => groupMessages?.group?.id && navigate(`/message/group-info/${groupMessages.group.id}`)}
                                        className="text-[10px] font-normal text-gray-400 dark:text-gray-800 cursor-pointer"
                                    >
                                        {groupMessages?.group?.member_count ? `${groupMessages.group.member_count} Members` : "View Group Members"}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="col-span-1 flex items-center justify-end">
                            <HiOutlineDotsVertical
                                onClick={() => setShowMenu(!showMenu)}
                                className="text-[22px] text-white dark:text-white cursor-pointer"
                            />
                            {showMenu && (
                                <div
                                    ref={menuRef}
                                    className="absolute top-8 right-2 bg-white dark:bg-gray-900 border border-gray-400 dark:border-gray-800 rounded-lg shadow-lg w-fit overflow-hidden"
                                >
                                    {menuItems.map((item, index) => (
                                        <p
                                            key={index}
                                            onClick={item.action}
                                            className={`text-[12px] font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 p-1.5 px-4 cursor-pointer 
                                                ${index === menuItems.length - 1 ? "" : "border-b border-gray-400 dark:border-gray-800"}`
                                            }
                                        >
                                            {item.label}
                                        </p>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="px-2.5 md:px-2 py-1 h-[88%] md:h-[70dvh] overflow-scroll no-scrollbar">
                    {/* Message List */}
                    {(!groupMessages.messages || groupMessages.messages.length === 0) && (
                        <div className="flex justify-center items-center h-full text-gray-400 dark:text-gray-700 text-sm">
                            No messages yet.
                        </div>
                    )}
                    {groupMessages.messages && groupMessages.messages.map((data, idx) => {
                        if (data.is_system) {
                            // System message: center, small, gray text
                            return (
                                <div key={data.id || idx} className="flex flex-col items-center justify-center my-2">
                                    <span className="text-[10px] text-center text-gray-400 dark:text-gray-700 pt-1 rounded">
                                        {data.description}
                                    </span>
                                    <span className="text-[8px] text-center text-gray-400 dark:text-gray-700 pb-1 rounded">
                                        {formatRelativeTime(data.created_at, now)}
                                    </span>
                                </div>
                            );
                        }
                        // Heart icon logic
                        const hasMessage = !!data.message;
                        const hasImage = !!data.image;
                        const hasVideo = !!data.video;
                        const hasMedia = hasImage || hasVideo;
                        const showHeartOnMessageOnly = hasMessage && hasMedia;
                        return (
                            <div key={data.id || idx}>
                                {/* Sender Message (current user) */}
                                {data.is_sent_by_current_user ? (
                                    <div className="flex flex-col items-end mb-1">
                                        <p className="text-gray-400 dark:text-gray-700 text-[8px]">
                                            {formatRelativeTime(data.created_at, now)}
                                        </p>

                                        {/* Image */}
                                        {hasImage && (
                                            <div className="relative">
                                                <img
                                                    alt="sent-img"
                                                    src={data.image}
                                                    className="w-fit h-fit max-h-[11rem] max-w-[7.5rem] rounded-lg mb-1 cursor-pointer"
                                                    onClick={() => {
                                                        setPopupType("image");
                                                        setPopupSrc(data.image);
                                                        setPopupOpen(true);
                                                    }}
                                                />
                                                {/* Show heart only if NOT both message+media */}
                                                {!showHeartOnMessageOnly && data.is_reacted && (
                                                    <GoHeartFill className="text-[12px] absolute -bottom-1 -right-1 text-red-500" />
                                                )}
                                            </div>
                                        )}

                                        {/* Video */}
                                        {hasVideo && (
                                            <div className="relative">
                                                <video
                                                    src={data.video}
                                                    disablePictureInPicture
                                                    className="w-fit h-fit max-h-[11rem] max-w-[7.5rem] rounded-lg mb-1 cursor-pointer"
                                                    onMouseEnter={e => {
                                                        if (e.target.paused) {
                                                            const playPromise = e.target.play();
                                                            if (playPromise !== undefined) {
                                                                playPromise.catch(() => { });
                                                            }
                                                        }
                                                        setHoveredVideo(data.id);
                                                    }}
                                                    onMouseLeave={e => {
                                                        if (!e.target.paused) {
                                                            e.target.pause();
                                                        }
                                                        e.target.currentTime = 0;
                                                        setHoveredVideo(null);
                                                    }}
                                                    onClick={() => {
                                                        setPopupType("video");
                                                        setPopupSrc(data.video);
                                                        setPopupOpen(true);
                                                    }}
                                                    muted
                                                    loop
                                                />
                                                {/* Show heart only if NOT both message+media */}
                                                {!showHeartOnMessageOnly && data.is_reacted && (
                                                    <GoHeartFill className="text-[12px] absolute -bottom-1 -right-1 text-red-500" />
                                                )}
                                            </div>
                                        )}

                                        {/* Message */}
                                        {hasMessage && (
                                            <div className="px-2 py-1.5 bg-gradient-to-r from-[#02488C] to-[#3F196F] rounded-lg w-fit max-w-[70%] relative">
                                                <p className="text-[13px] text-white dark:text-white leading-[15px] select-none">
                                                    {data.message}
                                                </p>
                                                {/* Show heart if: single message, or both message+media */}
                                                <div className="absolute top-1/2 -left-5 transform -translate-y-1/2">
                                                    {((showHeartOnMessageOnly && data.is_reacted) || (!hasMedia && data.is_reacted)) && (
                                                        <GoHeartFill className="text-[16px] text-red-500 dark:text-red-500 cursor-pointer" />
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Reaction display for current user's message */}
                                        <div className="relative">
                                            {data.reaction_count > 0 && (
                                                <div className="flex mt-[1px] items-center gap-x-1 bg-black dark:bg-gray-900 rounded-full px-1.5 py-0.5">
                                                    <GoHeartFill className="text-red-500 text-[12px]" />
                                                    <span className="text-white text-xs">{data.reaction_count}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    // Receiver Message (other user)
                                    <div className="flex flex-col items-start mb-3">
                                        <div className="flex items-center gap-1">
                                            <img
                                                alt={data.created_by.full_name || "User"}
                                                src={data.created_by.avatar_url || DEFAULT_GROUP_IMAGE}
                                                className="h-6 w-6 rounded-full object-cover border border-gray-400 dark:border-gray-800"
                                            />
                                            <div className="flex flex-col">
                                                <p className="text-[10px] font-semibold text-black dark:text-white">
                                                    {data.created_by.full_name || "Unknown User"}
                                                </p>
                                                <p className="text-gray-400 dark:text-gray-700 text-[8px] mt-[-2px]">
                                                    {formatRelativeTime(data.created_at, now)}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Image */}
                                        {hasImage && (
                                            <div className="relative mt-1">
                                                <img
                                                    alt="received-img"
                                                    src={data.image}
                                                    className="w-fit h-fit max-h-[11rem] max-w-[7.5rem] rounded-lg cursor-pointer"
                                                    onClick={() => {
                                                        setPopupType("image");
                                                        setPopupSrc(data.image);
                                                        setPopupOpen(true);
                                                    }}
                                                />
                                                {/* Show heart only if NOT both message+media */}
                                                {!showHeartOnMessageOnly && (
                                                    <div className="absolute top-1/2 -right-5 transform -translate-y-1/2">
                                                        {data.is_reacted ? (
                                                            <GoHeartFill
                                                                onClick={() => handleReaction(data.id, false)}
                                                                className="text-[16px] text-red-500 dark:text-red-500 cursor-pointer"
                                                            />
                                                        ) : (
                                                            <GoHeart
                                                                onClick={() => handleReaction(data.id, false)}
                                                                className="text-[16px] text-gray-400 dark:text-gray-700 cursor-pointer"
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Video */}
                                        {hasVideo && (
                                            <div className="relative mt-1">
                                                <video
                                                    loop
                                                    muted
                                                    src={data.video}
                                                    disablePictureInPicture
                                                    className="w-fit h-fit max-h-[11rem] max-w-[7.5rem] rounded-lg cursor-pointer"
                                                    onMouseEnter={e => {
                                                        if (e.target.paused) {
                                                            const playPromise = e.target.play();
                                                            if (playPromise !== undefined) {
                                                                playPromise.catch(() => { });
                                                            }
                                                        }
                                                        setHoveredVideo(data.id);
                                                    }}
                                                    onMouseLeave={e => {
                                                        if (!e.target.paused) {
                                                            e.target.pause();
                                                        }
                                                        e.target.currentTime = 0;
                                                        setHoveredVideo(null);
                                                    }}
                                                    onClick={() => {
                                                        setPopupType("video");
                                                        setPopupSrc(data.video);
                                                        setPopupOpen(true);
                                                    }}
                                                />
                                                {/* Show heart only if NOT both message+media */}
                                                {!showHeartOnMessageOnly && (
                                                    <div className="absolute top-1/2 -right-5 transform -translate-y-1/2">
                                                        {data.is_reacted ? (
                                                            <GoHeartFill
                                                                onClick={() => handleReaction(data.id, false)}
                                                                className="text-[16px] text-red-500 dark:text-red-500 cursor-pointer"
                                                            />
                                                        ) : (
                                                            <GoHeart
                                                                onClick={() => handleReaction(data.id, false)}
                                                                className="text-[16px] text-gray-400 dark:text-gray-700 cursor-pointer"
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Message */}
                                        {hasMessage && (
                                            <div className="px-2 mt-1 py-1.5 bg-gradient-to-r from-[#02488C] to-[#3F196F] rounded-lg w-fit max-w-[70%] relative">
                                                <p className="text-[13px] text-white dark:text-white leading-[15px] select-none">
                                                    {data.message}
                                                </p>

                                                {/* Show heart if: single message, or both message+media */}
                                                {((showHeartOnMessageOnly) || (!hasMedia)) && (
                                                    <div className="absolute top-1/2 -right-5 transform -translate-y-1/2">
                                                        {data.is_reacted ? (
                                                            <GoHeartFill
                                                                onClick={() => handleReaction(data.id, false)}
                                                                className="text-[16px] text-red-500 dark:text-red-500 cursor-pointer"
                                                            />
                                                        ) : (
                                                            <GoHeart
                                                                onClick={() => handleReaction(data.id, false)}
                                                                className="text-[16px] text-gray-400 dark:text-gray-700 cursor-pointer"
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Reaction display for others' messages */}
                                        <div className="relative">
                                            {data.reaction_count > 0 && (
                                                <div className="flex mt-[1px] items-center gap-x-1 bg-black dark:bg-gray-900 rounded-full px-1.5 py-0.5">
                                                    <GoHeartFill className="text-[12px] text-red-500" />
                                                    <span className="text-white text-[11px]">{data.reaction_count}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    <div ref={messagesEndRef} />
                </div>

                {(imagePreview || videoPreview) && (
                    <div className="sticky bottom-14 border-t border-gray-400 dark:border-gray-800 bg-white dark:bg-black flex gap-2 items-center px-2 py-1">
                        {imagePreview && (
                            <div className="relative">
                                <img src={imagePreview} alt="preview" className="h-auto w-20 object-cover rounded border" />
                                <button type="button" onClick={handleRemoveImage} className="absolute top-0 right-0 bg-red-500 text-white rounded-full px-1 text-xs">
                                    <IoMdClose className="text-white" />
                                </button>
                            </div>
                        )}
                        {videoPreview && (
                            <div className="relative">
                                <video src={videoPreview} controls className="h-auto w-24 rounded border" />
                                <button
                                    type="button"
                                    disablePictureInPicture
                                    onClick={handleRemoveVideo}
                                    className="absolute flex items-center justify-center h-4 w-4 top-0 -right-5 bg-red-500 text-white rounded-full"
                                >
                                    <IoMdClose className="text-white text-[12px]" />
                                </button>
                            </div>
                        )}
                    </div>
                )}

                <div className="sticky bottom-0 bg-white dark:bg-black z-40 px-2.5 md:px-2 py-1 border-t border-gray-400 dark:border-gray-800">
                    <form className="flex items-center justify-between gap-1 my-1" onSubmit={handleSubmit} encType="multipart/form-data">
                        <div className="bg-gradient-to-r from-[#02488C] to-[#3F196F] p-2 rounded-full cursor-pointer z-50 border dark:border-gray-800" onClick={handleFileClick}>
                            <IoImages className="text-white" />
                            <input type="file" accept="image/*,video/*" ref={fileInputRef} className="hidden" onChange={handleFileChange} />
                        </div>
                        <div className="relative w-full">
                            <input
                                value={message}
                                placeholder="Send a message"
                                onChange={e => setMessage(e.target.value)}
                                className="text-[14px] w-full py-2 bg-gray-200 dark:bg-gray-900 text-black dark:text-white pl-2 pr-10 focus:outline-none rounded-md border-none"
                            />
                            <button
                                type="submit"
                                disabled={sending}
                                className="absolute top-1.5 right-1.5 bg-gradient-to-r from-[#02488C] to-[#3F196F] flex items-center justify-center h-[26px] w-[26px] rounded-full cursor-pointer z-50"
                            >
                                {sendGroupMessageLoading ? (
                                    <ClipLoader size={10} color="#ffffff" />
                                ) : (
                                    <IoSend className="text-[14px] text-white" />
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <DeleteModal
                isOpen={showDeleteChat}
                onClose={() => setShowDeleteChat(false)}
                onDelete={handleDelete}
            />

            <MediaPopup
                src={popupSrc}
                type={popupType}
                open={popupOpen}
                onClose={() => setPopupOpen(false)}
            />
        </Fragment>
    );
};

export default MessageGroupChatRoom;